import { Phone, MessageCircle, MapPin, ChevronRight, Clock } from "lucide-react";
import { Reveal, Overline } from "@/components/Reveal";
import InquiryForm from "@/components/InquiryForm";
import { BUSINESS } from "@/data/site";

const waLink = `https://wa.me/${BUSINESS.whatsapp}?text=${encodeURIComponent(BUSINESS.whatsappMessage)}`;

export default function Contact() {
  return (
    <section id="contact" data-testid="contact-section" className="relative bg-[var(--bg)] py-24 md:py-32">
      <div className="mx-auto max-w-[1400px] px-6 md:px-12">
        <nav aria-label="Breadcrumb" data-testid="contact-breadcrumb" className="mb-10 flex items-center gap-1.5 text-xs text-[var(--ink-soft)]">
          <a href="#home" className="hover:text-[var(--green)]">Home</a>
          <ChevronRight size={13} />
          <span className="font-semibold text-[var(--green)]">Contact</span>
        </nav>

        <div className="grid gap-14 lg:grid-cols-2 lg:gap-20">
          {/* Left: info + map */}
          <div>
            <Reveal>
              <Overline>Get In Touch</Overline>
              <h2 className="mt-5 font-display text-4xl font-bold leading-[1.05] tracking-tight text-[var(--ink)] sm:text-5xl">
                Let’s talk wholesale.
              </h2>
              <p className="mt-6 max-w-md text-[var(--ink-soft)]">
                Call, message on WhatsApp, or send us your requirement. We supply premium produce and spices
                in bulk across Karnataka.
              </p>
            </Reveal>

            <Reveal delay={0.1}>
              <div className="mt-9 space-y-4">
                <a href={`tel:${BUSINESS.phone}`} data-testid="contact-phone" className="flex items-center gap-4 rounded-2xl border border-[var(--border)] bg-white/60 p-5 transition-colors hover:border-[var(--green)]/40">
                  <span className="flex h-12 w-12 items-center justify-center rounded-xl bg-[var(--green)]/10 text-[var(--green)]"><Phone size={20} /></span>
                  <div>
                    <p className="text-xs uppercase tracking-wider text-[var(--ink-soft)]">Phone</p>
                    <p className="font-display text-xl font-semibold text-[var(--ink)]">{BUSINESS.phone}</p>
                  </div>
                </a>
                <div className="flex items-start gap-4 rounded-2xl border border-[var(--border)] bg-white/60 p-5">
                  <span className="flex h-12 w-12 shrink-0 items-center justify-center rounded-xl bg-[var(--gold)]/12 text-[var(--gold)]"><MapPin size={20} /></span>
                  <div>
                    <p className="text-xs uppercase tracking-wider text-[var(--ink-soft)]">Address</p>
                    <p className="mt-1 text-sm leading-relaxed text-[var(--ink)]">{BUSINESS.addressLines.join(" ")}</p>
                  </div>
                </div>
                <div className="flex items-start gap-4 rounded-2xl border border-[var(--border)] bg-white/60 p-5" data-testid="contact-hours">
                  <span className="flex h-12 w-12 shrink-0 items-center justify-center rounded-xl bg-[var(--green)]/10 text-[var(--green)]"><Clock size={20} /></span>
                  <div>
                    <p className="text-xs uppercase tracking-wider text-[var(--ink-soft)]">Business Hours</p>
                    <p className="mt-1 font-display text-lg font-semibold text-[var(--ink)]">7:00 AM – 8:00 PM</p>
                    <p className="text-sm text-[var(--ink-soft)]">Open Daily</p>
                  </div>
                </div>
              </div>
            </Reveal>

            <Reveal delay={0.15}>
              <div className="mt-5 flex flex-wrap gap-3">
                <a href={`tel:${BUSINESS.phone}`} data-testid="contact-call-btn" className="flex items-center gap-2 rounded-full bg-[var(--green)] px-6 py-3 text-sm font-semibold text-white transition-all hover:bg-[var(--green-deep)]">
                  <Phone size={16} /> Call Now
                </a>
                <a href={waLink} target="_blank" rel="noopener noreferrer" data-testid="contact-whatsapp-btn" className="flex items-center gap-2 rounded-full bg-[#25D366] px-6 py-3 text-sm font-semibold text-white transition-all hover:brightness-95">
                  <MessageCircle size={16} /> WhatsApp
                </a>
              </div>
            </Reveal>

            <Reveal delay={0.2}>
              <div className="mt-8 overflow-hidden rounded-2xl border border-[var(--border)]">
                <iframe
                  title="M N Savanur location"
                  data-testid="contact-map"
                  src={BUSINESS.mapsEmbed}
                  className="h-64 w-full"
                  loading="lazy"
                  referrerPolicy="no-referrer-when-downgrade"
                />
              </div>
            </Reveal>
          </div>

          {/* Right: form */}
          <Reveal delay={0.1}>
            <div className="rounded-3xl border border-[var(--border)] bg-white/70 p-7 shadow-xl shadow-black/5 md:p-9">
              <h3 className="font-display text-2xl font-semibold text-[var(--ink)]">Request a Quote</h3>
              <p className="mb-6 mt-1 text-sm text-[var(--ink-soft)]">Fill in your requirement and we’ll respond with wholesale pricing.</p>
              <InquiryForm />
            </div>
          </Reveal>
        </div>
      </div>
    </section>
  );
}
